import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  IconButton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem,
  Divider,
  Chip,
  Tooltip,
  Collapse
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutline,
  SearchOutlined,
  AddOutlined,
  PaymentOutlined,
  CloseOutlined,
  HistoryOutlined
} from "@mui/icons-material";
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc, 
  Timestamp 
} from 'firebase/firestore';
import Swal from "sweetalert2";
import { db } from '../../firebase'; // Adjust this path to your firebase config layout

const PAGE_SIZE = 5;

const getTodayString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function AddLoan() {
  const [loans, setLoans] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  // Parent Loan Form State
  const [form, setForm] = useState({
    loanTitle: "",
    loanType: "",
    description: "",
    totalAmount: "",
    paidAmount: 0,
    status: "Pending"
  });

  // Payment Tracking States
  const [activePaymentLoanId, setActivePaymentLoanId] = useState(null);
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(null); 
  const [paymentForm, setPaymentForm] = useState({
    payAmount: "",
    payDescription: "",
    payMode: "Cash",
    payDate: getTodayString()
  });

  const loansCollectionRef = collection(db, 'loans');

  const fetchLoans = async () => {
    try {
      const snap = await getDocs(loansCollectionRef);
      setLoans(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (error) {
      console.error("Error fetching loans: ", error);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePaymentFormChange = (e) => {
    setPaymentForm({ ...paymentForm, [e.target.name]: e.target.value });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setForm({
      loanTitle: "",
      loanType: "",
      description: "",
      totalAmount: "",
      paidAmount: 0,
      status: "Pending"
    });
  };

  // Helper utility to update loan financial aggregation structures cleanly
  const recalculateAndSaveLoan = async (loanId, targetLoan, updatedHistory) => {
    const total = parseFloat(targetLoan.totalAmount || 0);
    const totalPaid = updatedHistory.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    const remaining = total - totalPaid;
    
    let computedStatus = 'Pending';
    if (totalPaid > 0) {
      computedStatus = remaining <= 0 ? 'Paid' : 'Partially Paid';
    }

    const docRef = doc(db, 'loans', loanId);
    await setDoc(docRef, {
      ...targetLoan,
      paidAmount: totalPaid,
      remainingAmount: remaining,
      status: computedStatus,
      paymentHistory: updatedHistory,
      updatedAt: Timestamp.now()
    }, { merge: true });

    fetchLoans();
  };

  // Save or Create Main Loan
  const saveLoan = async (e) => {
    e.preventDefault();
    const { loanTitle, loanType, description, totalAmount, paidAmount, status } = form;

    if (!loanTitle || !loanType || !totalAmount) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    try {
      const id = editingId || doc(loansCollectionRef).id;
      const total = parseFloat(totalAmount);
      const baselinePaid = editingId ? parseFloat(form.paidAmount || 0) : parseFloat(paidAmount || 0);
      const remaining = total - baselinePaid;

      const baseHistory = editingId ? (form.paymentHistory || []) : [];
      
      // If adding a brand new loan entry with an initial paid value, seed payment history
      if (!editingId && baselinePaid > 0 && baseHistory.length === 0) {
        baseHistory.push({
          date: new Date().toLocaleDateString('en-GB'),
          amount: baselinePaid,
          description: "Initial matching payment receipt",
          payMode: "Cash"
        });
      }

      const loanData = {
        loanTitle,
        loanType,
        description,
        totalAmount: total,
        paidAmount: baselinePaid,
        remainingAmount: remaining,
        status: remaining <= 0 ? 'Paid' : status,
        createdTime: editingId ? (form.createdTime || Timestamp.now()) : Timestamp.now(),
        paymentHistory: baseHistory
      };

      await setDoc(doc(db, 'loans', id), loanData, { merge: true });

      Swal.fire({
        title: "Success",
        text: editingId ? "Updated asset parameter profile" : "Logged structural liability record",
        icon: "success",
        timer: 1500,
        showConfirmButton: false
      });

      handleCloseDialog();
      fetchLoans();
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const editLoan = (loan) => {
    setForm({
      loanTitle: loan.loanTitle || "",
      loanType: loan.loanType || "",
      description: loan.description || "",
      totalAmount: loan.totalAmount || "",
      paidAmount: loan.paidAmount || 0,
      status: loan.status || "Pending",
      createdTime: loan.createdTime,
      paymentHistory: loan.paymentHistory || []
    });
    setEditingId(loan.id);
    setOpenDialog(true);
  };

  const deleteLoan = async (id) => {
    const res = await Swal.fire({
      title: "Delete Registry Record?",
      text: "This action cannot be reverted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it"
    });

    if (!res.isConfirmed) return;

    try {
      await deleteDoc(doc(db, 'loans', id));
      Swal.fire({ title: "Deleted", text: "Removed entry systematically.", icon: "success", timer: 1500, showConfirmButton: false });
      fetchLoans();
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  // Process payment additions or modifications
  const handlePaymentSubmit = async (e, loan) => {
    e.preventDefault();
    const pAmount = parseFloat(paymentForm.payAmount);
    if (!pAmount || pAmount <= 0) {
      Swal.fire("Error", "Enter valid payment allocation", "error");
      return;
    }

    const rawDate = new Date(paymentForm.payDate);
    const formattedDate = isNaN(rawDate.getTime()) 
      ? new Date().toLocaleDateString('en-GB') 
      : rawDate.toLocaleDateString('en-GB');

    const paymentPayload = {
      date: formattedDate,
      amount: pAmount,
      description: paymentForm.payDescription,
      payMode: paymentForm.payMode
    };

    let updatedHistory = [...(loan.paymentHistory || [])];

    if (editingPaymentIndex !== null) {
      // Update existing item index
      updatedHistory[editingPaymentIndex] = paymentPayload;
    } else {
      // Append item
      updatedHistory.push(paymentPayload);
    }

    try {
      await recalculateAndSaveLoan(loan.id, loan, updatedHistory);

      Swal.fire({
        title: "Logged",
        text: editingPaymentIndex !== null ? "Payment line revised" : `Captured ₹${pAmount}.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false
      });

      setPaymentForm({ payAmount: "", payDescription: "", payMode: "Cash", payDate: getTodayString() });
      setEditingPaymentIndex(null);
      fetchLoans();
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  // Pre-populate fields into sub-ledger form to edit an execution step
  const initEditPayment = (idx, historyItem) => {
    setEditingPaymentIndex(idx);
    
    // Convert back 'DD/MM/YYYY' format cleanly to standard text inputs fields 'YYYY-MM-DD'
    let inputDateStr = getTodayString();
    if (historyItem.date) {
      const parts = historyItem.date.split('/');
      if (parts.length === 3) {
        inputDateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    setPaymentForm({
      payAmount: historyItem.amount,
      payDescription: historyItem.description || "",
      payMode: historyItem.payMode || "Cash",
      payDate: inputDateStr
    });
  };

  // Delete structured inner transaction logs completely
  const deletePayment = async (idx, loan) => {
    const confirmation = await Swal.fire({
      title: "Remove Payment Entry?",
      text: "This adjustment shifts outstanding loan calculations.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Remove"
    });

    if (!confirmation.isConfirmed) return;

    try {
      const updatedHistory = (loan.paymentHistory || []).filter((_, index) => index !== idx);
      await recalculateAndSaveLoan(loan.id, loan, updatedHistory);
      
      Swal.fire({ title: "Removed", text: "Entry cleared.", icon: "success", timer: 1200, showConfirmButton: false });
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const filtered = loans.filter(
    (l) =>
      l.loanTitle?.toLowerCase().includes(search.toLowerCase()) ||
      l.loanType?.toLowerCase().includes(search.toLowerCase())
  );
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box minHeight="100vh" bgcolor="#f8fafc" p={{ xs: 1.5, md: 2 }}>
      
      {/* ================= COMPACT HEADER SECTION ================= */}
      <Card 
        elevation={0} 
        sx={{ 
          p: 1.5, 
          mb: 2, 
          background: "rgba(255, 255, 255, 0.9)", 
          backdropFilter: "blur(8px)",
          borderRadius: "8px", 
          border: "1px solid #e2e8f0",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1" fontWeight={800} color="#1e293b" sx={{ lineHeight: 1.2 }}>
              Loan Management Registry
            </Typography>
            <Typography variant="caption" color="#64748b">
              Monitor dynamic liabilities and manage sub-payment histories.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            size="small"
            startIcon={<AddOutlined sx={{ fontSize: '1rem !important' }} />}
            onClick={() => setOpenDialog(true)}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              px: 2,
              py: 0.5,
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
            }}
          >
            Create New Entry
          </Button>
        </Box>
      </Card>

      {/* ================= DATA GRID CONTAINER ================= */}
      <Card elevation={0} sx={{ borderRadius: "8px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
        
        {/* Compressed Search Field Container */}
        <Box p={1.5} bgcolor="#fff" borderBottom="1px solid #f1f5f9">
          <TextField
            placeholder="Search details..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: "#94a3b8", fontSize: "1.1rem" }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              maxWidth: "320px",
              width: "100%",
            }}
          />
        </Box>

        {/* Dense Table Framework */}
        <Box overflow="x-auto">
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1, fontSize: "0.8rem" }}>Sr No</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1, fontSize: "0.8rem" }}>Loan Title</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1, fontSize: "0.8rem" }}>Classification</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1, fontSize: "0.8rem" }}>Total Loan</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1, fontSize: "0.8rem" }}>Paid Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1, fontSize: "0.8rem" }}>Remaining Loan Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1, fontSize: "0.8rem" }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569", py: 1, fontSize: "0.8rem" }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody bgcolor="#fff">
              {paginated.length > 0 ? (
                paginated.map((loan, index) => {
                  const isPaymentRowOpen = activePaymentLoanId === loan.id;
                  return (
                    <React.Fragment key={loan.id}>
                      <TableRow hover sx={{ "& td": { py: 0.75, fontSize: "0.8rem" }, "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell sx={{ color: "#64748b", fontWeight: 500 }}>
                          {String((page - 1) * PAGE_SIZE + index + 1).padStart(2, '0')}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="#1e293b" sx={{ fontSize: "0.8rem" }}>{loan.loanTitle}</Typography>
                          <Typography variant="caption" color="#94a3b8" noWrap sx={{ display: 'block', maxWidth: '160px', fontSize: "0.7rem" }}>
                            {loan.description || 'No baseline notes'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box component="span" sx={{ px: 0.8, py: 0.2, bgcolor: "#f1f5f9", color: "#475569", borderRadius: "4px", fontWeight: 700, fontSize: "0.7rem" }}>
                            {loan.loanType}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: "#1e293b", fontWeight: 600 }}>₹{loan.totalAmount?.toLocaleString()}</TableCell>
                        <TableCell sx={{ color: "#10b981", fontWeight: 500 }}>₹{loan.paidAmount?.toLocaleString()}</TableCell>
                        <TableCell sx={{ color: loan.remainingAmount > 0 ? "#ef4444" : "#64748b", fontWeight: 600 }}>
                          ₹{loan.remainingAmount?.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={loan.status} 
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              height: "18px",
                              color: "#fff",
                              bgcolor: loan.status === 'Paid' ? '#10b981' : loan.status === 'Partially Paid' ? '#f59e0b' : '#ef4444'
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton onClick={() => editLoan(loan)} size="small" sx={{ color: "#3b82f6", p: 0.5 }}>
                            <EditOutlined sx={{ fontSize: "1.05rem" }} />
                          </IconButton>
                          <IconButton onClick={() => deleteLoan(loan.id)} size="small" sx={{ color: "#ef4444", p: 0.5 }}>
                            <DeleteOutline sx={{ fontSize: "1.05rem" }} />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={() => {
                              setActivePaymentLoanId(isPaymentRowOpen ? null : loan.id);
                              setEditingPaymentIndex(null);
                              setPaymentForm({ payAmount: "", payDescription: "", payMode: "Cash", payDate: getTodayString() });
                            }} 
                            sx={{ color: isPaymentRowOpen ? "#64748b" : "#10b981", p: 0.5 }}
                          >
                            {isPaymentRowOpen ? <CloseOutlined sx={{ fontSize: "1.05rem" }} /> : <PaymentOutlined sx={{ fontSize: "1.05rem" }} />}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* ================= ADJUSTABLE PAYMENT HISTORY LEDGER DRAWER ================= */}
                      <TableRow>
                        <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0, borderBottom: isPaymentRowOpen ? '1px solid #e2e8f0' : 'none' }}>
                          <Collapse in={isPaymentRowOpen} timeout="auto" unmountOnExit>
                            <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2} p={1.5} bgcolor="#f8fafc" borderRadius="6px" my={1} border="1px solid #e2e8f0">
                              
                              {/* Dynamic Input / Edit Payment Target Panel */}
                              <Box component="form" onSubmit={(e) => handlePaymentSubmit(e, loan)} display="flex" flexDirection="column" gap={1} minWidth={{ xs: "100%", md: "230px" }}>
                                <Typography variant="caption" fontWeight={800} color="#1e293b" display="flex" alignItems="center" gap={0.5}>
                                  <PaymentOutlined sx={{ fontSize: "0.9rem" }} color="primary" /> 
                                  {editingPaymentIndex !== null ? "Revise Remittance Log" : "Record Remittance"}
                                </Typography>
                                <TextField
                                  label="Amount"
                                  name="payAmount"
                                  type="number"
                                  size="small"
                                  value={paymentForm.payAmount}
                                  onChange={handlePaymentFormChange}
                                  required
                                  fullWidth
                                  sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.75rem" } }}
                                />
                                <TextField
                                  label="Note"
                                  name="payDescription"
                                  size="small"
                                  value={paymentForm.payDescription}
                                  onChange={handlePaymentFormChange}
                                  fullWidth
                                  sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.75rem" } }}
                                />
                                <TextField
                                  select
                                  label="Payment Mode"
                                  name="payMode"
                                  size="small"
                                  value={paymentForm.payMode}
                                  onChange={handlePaymentFormChange}
                                  fullWidth
                                  sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.75rem" } }}
                                >
                                  <MenuItem value="Cash" sx={{ fontSize: "0.75rem" }}>Cash</MenuItem>
                                  <MenuItem value="Bank Transfer" sx={{ fontSize: "0.75rem" }}>Bank Wire</MenuItem>
                                  <MenuItem value="Credit Card" sx={{ fontSize: "0.75rem" }}>Credit Card</MenuItem>
                                </TextField>

                                <TextField
                                  label="Payment Date"
                                  name="payDate"
                                  type="date"
                                  size="small"
                                  value={paymentForm.payDate}
                                  onChange={handlePaymentFormChange}
                                  required
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.75rem" } }}
                                />

                                <Box display="flex" gap={1}>
                                  {editingPaymentIndex !== null && (
                                    <Button 
                                      size="small" 
                                      variant="outlined" 
                                      color="inherit"
                                      onClick={() => {
                                        setEditingPaymentIndex(null);
                                        setPaymentForm({ payAmount: "", payDescription: "", payMode: "Cash", payDate: getTodayString() });
                                      }}
                                      sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "6px", flex: 1 }}
                                    >
                                      Cancel
                                    </Button>
                                  )}
                                  <Button 
                                    type="submit" 
                                    variant="contained" 
                                    size="small" 
                                    sx={{ 
                                      textTransform: "none", 
                                      fontWeight: 700, 
                                      fontSize: "0.75rem", 
                                      borderRadius: "6px", 
                                      py: 0.4, 
                                      flex: 2,
                                      bgcolor: editingPaymentIndex !== null ? "#3b82f6" : "#10b981", 
                                      "&:hover": { bgcolor: editingPaymentIndex !== null ? "#2563eb" : "#059669" } 
                                    }}
                                  >
                                    {editingPaymentIndex !== null ? "Apply Edit" : "Post Payment"}
                                  </Button>
                                </Box>
                              </Box>

                              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

                              {/* Payment History Render Node */}
                              <Box flexGrow={1}>
                                <Typography variant="caption" fontWeight={800} color="#1e293b" mb={1} display="flex" alignItems="center" gap={0.5}>
                                  <HistoryOutlined sx={{ fontSize: "0.9rem" }} color="secondary" /> Payment Ledger Run
                                </Typography>
                                {loan.paymentHistory && loan.paymentHistory.length > 0 ? (
                                  <Box display="flex" flexDirection="column" gap={0.5} maxHeight="200px" style={{ overflowY: 'auto' }}>
                                    {loan.paymentHistory.map((history, idx) => (
                                      <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" p={0.8} bgcolor="#fff" borderRadius="6px" border="1px solid #e2e8f0">
                                        <Box>
                                          <Typography variant="caption" fontWeight={700} color="#334155" sx={{ fontSize: "0.75rem" }}>
                                            Amount: ₹{history.amount?.toLocaleString()}
                                          </Typography>
                                          <Typography variant="caption" color="#64748b" sx={{ display: 'block', fontSize: "0.65rem" }}>
                                            Mode: <i>{history.payMode}</i> {history.description && `• Ref: ${history.description}`}
                                          </Typography>
                                          <Typography variant="caption" color="#94a3b8" sx={{ fontSize: "0.65rem", display: 'block' }}>
                                            Logged Date: {history.date}
                                          </Typography>
                                        </Box>
                                        <Box display="flex" gap={0.25}>
                                          <Tooltip title="Modify Payment">
                                            <IconButton size="small" onClick={() => initEditPayment(idx, history)} sx={{ color: "#3b82f6", p: 0.4 }}>
                                              <EditOutlined sx={{ fontSize: "0.95rem" }} />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete Record">
                                            <IconButton size="small" onClick={() => deletePayment(idx, loan)} sx={{ color: "#ef4444", p: 0.4 }}>
                                              <DeleteOutline sx={{ fontSize: "0.95rem" }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                ) : (
                                  <Box display="flex" alignItems="center" justifyContent="center" height="70px" bgcolor="#fff" borderRadius="6px" border="1px dashed #cbd5e1">
                                    <Typography variant="caption" color="#94a3b8" sx={{ fontSize: "0.7rem" }}>No structural logs verified.</Typography>
                                  </Box>
                                )}
                              </Box>

                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: "#94a3b8", fontSize: "0.8rem" }}>
                    No valid records located.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination Wrap */}
        {filtered.length > 0 && (
          <Box display="flex" justifyContent="center" py={1.5} bgcolor="#fff" borderTop="1px solid #f1f5f9">
            <Pagination
              count={Math.ceil(filtered.length / PAGE_SIZE)}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              size="small"
              sx={{ "& .MuiPaginationItem-root": { borderRadius: "6px", fontWeight: 600 } }}
            />
          </Box>
        )}
      </Card>

      {/* ================= DIALOG PARAMETERS ================= */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "10px", p: 0.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: "#1e293b", pb: 0.5, fontSize: "1rem" }}>
          {editingId ? "Modify Asset Parameters" : "Register New Liability"}
        </DialogTitle>
        <DialogContent>
          <Box mt={1.5} display="flex" flexDirection="column" gap={1.8}>
            <TextField
              label="Loan Title"
              name="loanTitle"
              size="small"
              value={form.loanTitle}
              onChange={handleFormChange}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
            />
            <TextField
              label="Classification Type"
              name="loanType"
              size="small"
              value={form.loanType}
              onChange={handleFormChange}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
            />
            <TextField
              label="Description Note"
              name="description"
              size="small"
              value={form.description}
              onChange={handleFormChange}
              fullWidth
              multiline
              rows={1.5}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
            />
            <TextField
              label="Total Loan (₹)"
              name="totalAmount"
              type="number"
              size="small"
              value={form.totalAmount}
              onChange={handleFormChange}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
            />
            
            {!editingId && (
              <TextField
                label="Paid Amount (₹)"
                name="paidAmount"
                type="number"
                size="small"
                value={form.paidAmount}
                onChange={handleFormChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
              />
            )}

            <TextField
              select
              label="Operational Progress State"
              name="status"
              size="small"
              value={form.status}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
            >
              <MenuItem value="Pending" sx={{ fontSize: "0.8rem" }}>Pending Audit</MenuItem>
              <MenuItem value="Partially Paid" sx={{ fontSize: "0.8rem" }}>Amortization Active</MenuItem>
              <MenuItem value="Paid" sx={{ fontSize: "0.8rem" }}>Fully Settled</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 1.5, pt: 0.5, gap: 0.5 }}>
          <Button onClick={handleCloseDialog} color="inherit" size="small" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}>
            Dismiss
          </Button>
          <Button 
            variant="contained" 
            size="small"
            onClick={saveLoan}
            sx={{ 
              textTransform: "none", 
              fontWeight: 600, 
              fontSize: "0.75rem",
              borderRadius: "6px",
              px: 2,
              background: editingId ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            }}
          >
            {editingId ? "Apply Changes" : "Save Liability"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddLoan;